<template>
  <b-overlay :show="pageBusy" variant="transparent">
    <b-card v-if="isCraDirectory">
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <div class="pr-2 pb-1">
          <h4 class="mb-1">CRA registered charities</h4>
          <p class="mb-0 text-muted">{{ craSyncSummary }}</p>
        </div>
        <b-button
          v-if="$can('update', 'Charities')"
          variant="primary"
          class="mb-1"
          :disabled="craUpdateBusy || craUpdateRunning"
          @click="confirmCraUpdate"
        >
          <feather-icon icon="RefreshCwIcon" class="mr-50" />
          Update from CRA
        </b-button>
      </div>
    </b-card>
    <b-card v-else>
      <p class="mb-0 text-muted">
        Organizations a user has started. Pending creation means they have not finished creating it.
        Pending activation means they started activation and have not finished.
        The full CRA directory is under CRA registered charities.
      </p>
    </b-card>
    <b-card>
      <div class="row pt-0">
        <div class="col-12 pb-1">
          <span class="font-weight-bolder">Filters</span>
        </div>
        <div v-if="!isCraDirectory" class="col-lg-3 col-12 col-md-6">
          <label>Date Range</label>
          <flat-pickr
            v-model="range"
            class="form-control"
            :config="{ mode: 'range' }"
          />
        </div>
        <div class="col-lg-3 col-12 col-md-6">
          <label>Status</label>
          <v-select
            v-model="status"
            :reduce="e => e.value"
            :searchable="false"
            :options="statusOptions"
            :clearable="false"
          />
        </div>
        <div class="col-lg-3 col-12 col-md-6">
          <label>Country</label>
          <v-select
            v-model="countryId"
            :reduce="e => e.value"
            :options="countryOptions"
            :clearable="true"
            placeholder="All countries"
          />
        </div>
        <div class="col-lg-3 col-12 col-md-6">
          <label>Search</label>
          <input
            v-model="search"
            type="text"
            class="form-control"
            placeholder="Name, business number, or ID"
          >
        </div>
        <div v-if="!isCraDirectory" class="col-lg-3 col-12 col-md-6">
          <label>Activated by (user ID)</label>
          <input
            v-model="activatedBy"
            type="text"
            class="form-control"
            placeholder=""
          >
        </div>
        <div class="col-lg-3 col-12 col-md-6 pt-2 pt-lg-2" @click="filterCharities()">
          <span class="btn btn-primary">
            <feather-icon icon="SearchIcon" /> Search
          </span>
        </div>
      </div>
    </b-card>

    <b-card>
      <b-overlay :show="itemBusy">
        <b-table
          :key="tableKey"
          class="rounded border"
          :items="items"
          :fields="fields"
          :per-page="10"
          :current-page="1"
          responsive
        >
          <template #head()="scope">
            <div class="text-nowrap text-primary">
              {{ scope.label }}
            </div>
          </template>

          <template #cell(name)="data">
            <span v-if="data.item.id" class="text-nowrap">
              <div class="d-flex align-items-center">
                <div v-if="data.item.logoUrl" class="pr-1">
                  <b-img :src="data.item.logoUrl" rounded style="height: 36px; width: 36px; object-fit: cover;" />
                </div>
                <div class="w-100">
                  <div class="font-weight-bolder">{{ data.item.nameEn }}</div>
                  <small class="text-muted">{{ data.item.nameFr }}</small>
                </div>
              </div>
            </span>
          </template>

          <template #cell(status)="data">
            <span v-if="data.item.id" class="text-nowrap">
              <b-badge :variant="statusVariant(data.item)">
                {{ statusText(data.item) }}
              </b-badge>
            </span>
          </template>

          <template #cell(progress)="data">
            <span v-if="data.item.id" class="text-nowrap">
              {{ progressText(data.item) }}
            </span>
          </template>

          <template #cell(submittedBy)="data">
            <div v-if="data.item.id">
              <div class="font-weight-bolder text-nowrap">
                {{ data.item.submittedByName || data.item.activatedBy || '—' }}
              </div>
              <small v-if="data.item.submittedByEmail" class="text-muted">{{ data.item.submittedByEmail }}</small>
              <small
                v-else-if="data.item.activatedBy && data.item.submittedByName"
                class="text-muted"
              >{{ data.item.activatedBy }}</small>
            </div>
          </template>

          <template #cell(registrationKind)="data">
            <span v-if="data.item.id" class="text-nowrap">
              {{ registrationLabels[data.item.registrationKind] || 'CRA directory' }}
            </span>
          </template>

          <template #cell(registrationNumber)="data">
            <span v-if="data.item.id">{{ data.value || '—' }}</span>
          </template>

          <template #cell(requestedPlan)="data">
            <span v-if="data.item.id" class="text-nowrap">{{ planLabel(data.value) }}</span>
          </template>

          <template #cell(activatedBy)="data">
            <div v-if="data.item.id" class="text-nowrap pt-1">
              <span v-if="data.value" class="font-weight-bolder">{{ data.value }}</span>
              <span v-else class="text-muted">—</span>
            </div>
          </template>

          <template #cell(createdAt)="data">
            <div v-if="data.item.id" class="text-nowrap pt-1">
              {{ dayjs(data.value).format('MMM D, YYYY h:mm A') }}
            </div>
          </template>

          <template #cell(updatedAt)="data">
            <div v-if="data.item.id" class="text-nowrap pt-1">
              {{ dayjs(data.value).format('MMM D, YYYY h:mm A') }}
            </div>
          </template>

          <template #cell()="data">
            <div class="text-nowrap pt-1">
              {{ data.value }}
            </div>
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              v-if="data.item.id && $can('update', 'Charities')"
              size="lg"
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              boundary="viewport"
            >
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" />
              </template>
              <b-dropdown-item
                v-if="canReviewCharity(data.item)"
                @click="openReview(data.item)"
              >
                <feather-icon icon="EyeIcon" />
                Review
              </b-dropdown-item>
              <b-dropdown-item
                v-if="canDeactivateCharity(data.item)"
                variant="danger"
                @click="confirmDeactivate(data.item)"
              >
                <feather-icon icon="SlashIcon" />
                Deactivate
              </b-dropdown-item>
              <b-dropdown-item
                variant="danger"
                @click="confirmDelete(data.item)"
              >
                <feather-icon icon="TrashIcon" />
                Delete
              </b-dropdown-item>
              <b-dropdown-item @click="openAdvisory(data.item)">
                <feather-icon icon="UsersIcon" />
                Team &amp; activity
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-overlay>

      <div class="d-flex p-1">
        <div class="w-100">
          Page <span class="text-primary">{{ currentPage }}</span> of
          <span class="text-primary">{{ Math.ceil(totalRows / 10) || 1 }}</span>
        </div>
        <div class="w-100">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="10"
            aria-controls="charities-table"
            align="right"
            size="sm"
            class="my-0"
            @page-click="loadNewPage"
          />
        </div>
      </div>
    </b-card>

    <b-modal
      v-model="previewOpen"
      title="Review organization"
      size="lg"
      hide-footer
      @hidden="preview = null"
    >
      <b-overlay :show="previewBusy">
        <div v-if="preview">
          <div class="d-flex align-items-center mb-2">
            <b-img
              v-if="preview.logoUrl"
              :src="preview.logoUrl"
              class="mr-1"
              rounded
              style="height: 64px; width: 64px; object-fit: contain;"
            />
            <div>
              <div class="font-weight-bolder">{{ preview.nameEn }}</div>
              <small class="text-muted">{{ preview.nameFr }}</small>
            </div>
          </div>
          <p class="mb-1"><strong>Status:</strong> {{ statusText(preview) }}</p>
          <p class="mb-1"><strong>Registration:</strong> {{ registrationLabels[preview.registrationKind] || preview.registrationKind || 'CRA directory' }}</p>
          <p class="mb-1"><strong>CRA / business number:</strong> {{ preview.businessNumber || 'None' }}</p>
          <p class="mb-1"><strong>Registration or incorporation number:</strong> {{ preview.registrationNumber || '—' }}</p>
          <p class="mb-1">
            <strong>Assigned plan:</strong> {{ planLabel(preview.fiaasPlan) }}
          </p>
          <p class="mb-1">
            <strong>Requested plan:</strong> {{ planLabel(preview.requestedPlan || preview.activation.requestedPlan) }}
          </p>
          <p class="mb-1"><strong>Country:</strong> {{ preview.countryId }}</p>
          <p class="mb-1"><strong>Website:</strong> {{ preview.website || '—' }}</p>
          <p class="mb-1">
            <strong>Address:</strong>
            {{ [preview.addressLine, preview.city, preview.province, preview.postalCode].filter(Boolean).join(', ') || '—' }}
          </p>
          <p class="mb-2"><strong>About:</strong> {{ preview.descriptionEn || '—' }}</p>
          <hr>
          <p class="mb-1"><strong>Representative:</strong> {{ preview.activation.officialName }} — {{ preview.activation.position }}</p>
          <p class="mb-1"><strong>Email:</strong> {{ preview.activation.businessEmail }}</p>
          <p class="mb-1"><strong>Mobile:</strong> {{ preview.activation.mobile }}</p>
          <p class="mb-1">
            <strong>Bank:</strong>
            {{ preview.activation.bankInstitutionName || '—' }}
            <span v-if="preview.activation.bankLast4">•••• {{ preview.activation.bankLast4 }}</span>
          </p>
          <p v-if="preview.activation.bankChangePending" class="mb-1">
            <strong>Pending bank:</strong>
            {{ preview.activation.pendingBankInstitutionName || '—' }}
            <span v-if="preview.activation.pendingBankLast4">•••• {{ preview.activation.pendingBankLast4 }}</span>
            <b-button size="sm" class="ml-1" variant="primary" :disabled="previewBusy" @click="approveBankChange">
              {{ preview.activation.bankApprovalRequested ? 'Approve bank change' : 'Record bank approval' }}
            </b-button>
          </p>
          <p class="mb-2"><strong>Certification:</strong> {{ preview.activation.certificationText || '—' }}</p>
          <div v-if="preview.activation.signatureUrl" class="mb-2">
            <div class="font-weight-bolder mb-50">Signature</div>
            <b-img :src="preview.activation.signatureUrl" style="max-height: 80px; background: #fff; border: 1px solid #eee;" />
          </div>
          <b-form-group label="Rejection reason" label-for="rejection-reason">
            <b-form-textarea
              id="rejection-reason"
              v-model="rejectionReason"
              rows="3"
              placeholder="Required only if you reject this organization"
            />
          </b-form-group>
          <div class="d-flex justify-content-end">
            <b-button variant="outline-danger" class="mr-1" :disabled="previewBusy" @click="rejectPreview">
              Reject
            </b-button>
            <b-button variant="success" :disabled="previewBusy" @click="approvePreview">
              Approve
            </b-button>
          </div>
        </div>
      </b-overlay>
    </b-modal>

    <b-modal
      v-model="advisoryOpen"
      title="Team &amp; activity"
      size="lg"
      hide-footer
      @hidden="advisory = null"
    >
      <b-overlay :show="advisoryBusy">
        <div v-if="advisory">
          <p class="mb-1">
            <strong>Registration or incorporation number:</strong>
            {{ advisory.registrationNumber || '—' }}
          </p>
          <p class="mb-1">
            <strong>Assigned plan:</strong> {{ planLabel(advisory.fiaasPlan) }}
          </p>
          <p class="mb-1">
            <strong>Requested plan:</strong> {{ planLabel(advisory.requestedPlan || advisory.activation.requestedPlan) }}
          </p>
          <p class="mb-1">
            <strong>Official representative:</strong>
            {{ advisory.activation.officialName || '—' }}
          </p>
          <p class="mb-1">
            <strong>Gively advisory access:</strong>
            {{ givelyIsAdministrator ? 'Enabled by the official representative' : 'Not enabled' }}
          </p>

          <h6 class="mt-3">Team ({{ advisoryTeam.length }})</h6>
          <p v-if="!advisoryTeam.length" class="text-muted small mb-2">
            No team members. Only the official representative works on this account.
          </p>
          <b-table
            v-else
            :items="advisoryTeam"
            :fields="teamFields"
            small
            striped
            class="mb-3"
          >
            <template #cell(status)="row">
              <b-badge :variant="row.item.status === 'invited' ? 'warning' : 'success'">
                {{ row.item.status === 'invited' ? 'Invitation sent' : 'Active' }}
              </b-badge>
            </template>
            <template #cell(addedAt)="row">{{ formatActivityDate(row.item.addedAt) }}</template>
            <template #cell(userId)="row"><code class="small">{{ row.item.userId }}</code></template>
          </b-table>

          <h6 class="mt-3">Activity log</h6>
          <p class="text-muted small mb-2">
            Internal record of who did what on behalf of this charity. Not visible to the charity.
          </p>
          <p v-if="!advisoryActivity.length" class="text-muted small mb-2">No activity recorded yet.</p>
          <b-table
            v-else
            :items="advisoryActivity"
            :fields="activityFields"
            small
            striped
            responsive
            class="mb-3 charity-activity-table"
          >
            <template #cell(at)="row">{{ formatActivityDate(row.item.at) }}</template>
            <template #cell(action)="row">
              <span class="text-nowrap">{{ activityLabel(row.item.action) }}</span>
            </template>
            <template #cell(userName)="row">
              <div>{{ row.item.userName || '—' }}
                <b-badge v-if="row.item.isStaff" variant="dark" class="ml-1">Gively</b-badge>
              </div>
              <code class="small text-muted">{{ row.item.userId }}</code>
            </template>
            <template #cell(details)="row">
              <span class="small">{{ activityDetails(row.item.details) }}</span>
            </template>
          </b-table>

          <b-alert v-if="!givelyIsAdministrator" show variant="light">
            Gively can prepare this charity’s public profile and campaigns only after the official representative enables Gively advisory access from their Team section. Until then, their own team or the representative does that work.
          </b-alert>
          <div v-else>
            <p class="text-muted">
              Drafts you save here stay unpublished until the official representative approves them.
            </p>
            <h5 class="mb-1">Public profile draft</h5>
            <b-form-group label="About (English)">
              <b-form-textarea v-model="advisoryForm.descriptionEn" rows="4" />
            </b-form-group>
            <b-form-group label="Impact (English)">
              <b-form-textarea v-model="advisoryForm.impactEn" rows="3" />
            </b-form-group>
            <b-form-group label="Logo URL">
              <b-form-input v-model="advisoryForm.logoUrl" />
            </b-form-group>
            <b-form-checkbox v-model="advisoryForm.generalDonationsEnabled" class="mb-1" :disabled="advisory.status !== 'active'">
              Enable general donations
            </b-form-checkbox>
            <b-button size="sm" variant="primary" class="mb-2" :disabled="advisoryBusy" @click="saveAdvisoryProfile">
              Submit profile for charity approval
            </b-button>
            <b-alert v-if="advisory.pendingProfile && advisory.pendingProfile.status" show :variant="advisory.pendingProfile.status === 'rejected' ? 'danger' : 'warning'">
              Profile draft is {{ advisory.pendingProfile.status }}.
              <span v-if="advisory.pendingProfile.rejectionReason">{{ advisory.pendingProfile.rejectionReason }}</span>
            </b-alert>

            <hr>
            <h5 class="mb-1">Campaign draft</h5>
            <b-form-group label="Title">
              <b-form-input v-model="campaignForm.title" />
            </b-form-group>
            <b-form-group label="Story">
              <b-form-textarea v-model="campaignForm.story" rows="4" />
            </b-form-group>
            <b-form-group label="Target amount (USD)">
              <b-form-input v-model="campaignForm.targetAmount" type="number" min="1" />
            </b-form-group>
            <b-form-group label="Category">
              <b-form-select v-model="campaignForm.categoryId" :options="campaignCategories" />
            </b-form-group>
            <b-form-group label="Cover image URL (uses the charity logo if empty)">
              <b-form-input v-model="campaignForm.coverPhoto" />
            </b-form-group>
            <b-button size="sm" variant="primary" :disabled="advisoryBusy" @click="saveAdvisoryCampaign">
              Submit campaign for charity approval
            </b-button>

            <div v-if="advisoryCampaigns.length" class="mt-2">
              <h6>Waiting on the charity</h6>
              <ul class="mb-0">
                <li v-for="item in advisoryCampaigns" :key="item.id">
                  {{ item.title }} — {{ item.publicationStatus || item.contentStatus || 'submitted' }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </b-overlay>
    </b-modal>
    <b-modal id="bank-step-up" v-model="bankPrompt" title="Approve bank change" ok-title="Confirm" cancel-title="Cancel" centered @ok="confirmBankChange">
      <label class="d-block">Enter your authenticator or recovery code</label>
      <b-form-input v-model="bankCode" autocomplete="one-time-code" />
    </b-modal>
  </b-overlay>
</template>

<script>
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import dayjs from 'dayjs'
import flatPickr from 'vue-flatpickr-component'
import Utils from '@/utils/index'
import commonApi from '@/services/api'
import API from './api'

const toast = useToast()

export default {
  name: 'Charities',
  components: {
    flatPickr,
    vSelect,
  },
  data() {
    return {
      dayjs,
      currentPage: 1,
      totalRows: 10,
      range: '',
      searchDate: '',
      search: '',
      activatedBy: '',
      status: '',
      countryId: null,
      countryOptions: [],
      statusLabels: {
        active: 'Active',
        seeded: 'Seeded',
        pending: 'Pending',
        under_review: 'Awaiting review',
        rejected: 'Rejected',
      },
      workflowLabels: {
        pending_creation: 'Pending creation',
        pending_activation: 'Pending activation',
        under_review: 'Awaiting review',
        active: 'Active',
        rejected: 'Rejected',
        seeded: 'Not started',
      },
      directoryStatusLabels: {
        seeded: 'In CRA list',
        pending: 'Pending activation',
        under_review: 'Awaiting review',
        active: 'Active',
        rejected: 'Rejected',
      },
      stepLabels: {
        contact: 'Contact details',
        otp: 'Email and SMS verification',
        certification: 'Certification',
        bank: 'Bank account',
        complete: 'Activation finished',
      },
      statusVariants: {
        active: 'success',
        seeded: 'secondary',
        pending: 'warning',
        pending_creation: 'warning',
        pending_activation: 'warning',
        under_review: 'warning',
        rejected: 'danger',
      },
      registrationLabels: {
        cra_listed: 'CRA directory',
        cra_self: 'CRA number, not in directory',
        unregistered: 'No CRA number',
      },
      planLabels: {
        free: 'Free',
        growth: 'Growth',
        enterprise: 'Enterprise',
      },
      previewOpen: false,
      previewBusy: false,
      preview: null,
      rejectionReason: '',
      bankPrompt: false,
      bankCode: '',
      advisoryOpen: false,
      advisoryBusy: false,
      advisory: null,
      advisoryCampaigns: [],
      advisoryForm: {
        descriptionEn: '',
        impactEn: '',
        logoUrl: '',
        generalDonationsEnabled: false,
      },
      campaignForm: {
        title: '',
        story: '',
        targetAmount: '',
        categoryId: 'other',
        coverPhoto: '',
      },
      campaignCategories: [
        { value: 'other', text: 'Other' },
        { value: 'corporate_fundraiser', text: 'Corporate Fundraiser' },
        { value: 'giving_tuesday', text: 'Giving Tuesday' },
        { value: 'humanitarian_trip', text: 'Humanitarian Trip' },
        { value: 'in_memoriam', text: 'In Memoriam' },
        { value: 'anniversary', text: 'Anniversary' },
      ],
      items: [],
      tableKey: 0,
      removedCharityIds: {},
      charityListRequest: 0,
      searchParams: {},
      itemBusy: false,
      pageBusy: false,
      craSync: null,
      craUpdateBusy: false,
      craPoll: null,
    }
  },
  computed: {
    isCraDirectory() {
      return this.$route.meta.charityListScope === 'cra'
    },
    craUpdateRunning() {
      return Boolean(this.craSync && (this.craSync.running || this.craSync.status === 'running'))
    },
    craSyncSummary() {
      const sync = this.craSync
      if (!sync || sync.status === 'idle') {
        return 'This list is not refreshed on its own. Update it from the CRA when a newly registered charity is missing. After the update, that charity is included when someone searches to activate a charity on the website.'
      }
      if (sync.running || sync.status === 'running') {
        return 'Updating from the CRA registry. This can take several minutes. New charities show up here and on the website search when it finishes.'
      }
      if (sync.status === 'failed') {
        return sync.error || 'The last update from the CRA failed. Run it again.'
      }
      if (sync.finishedAt) {
        const when = this.dayjs(sync.finishedAt).format('MMM D, YYYY h:mm A')
        const added = Number(sync.added || 0)
        return `Last updated ${when}. ${added.toLocaleString()} charities were added in that update. The list does not refresh on its own. New charities are included in the website search used to activate a charity.`
      }
      return 'This list is not refreshed on its own. Update it from the CRA when a newly registered charity is missing.'
    },
    statusOptions() {
      if (this.isCraDirectory) {
        return [
          { label: 'All', value: '' },
          { label: 'In CRA list only', value: 'seeded' },
          { label: 'Pending activation', value: 'pending' },
          { label: 'Awaiting review', value: 'under_review' },
          { label: 'Active', value: 'active' },
          { label: 'Rejected', value: 'rejected' },
        ]
      }
      return [
        { label: 'All', value: '' },
        { label: 'Pending creation', value: 'pending_creation' },
        { label: 'Pending activation', value: 'pending_activation' },
        { label: 'Awaiting review', value: 'under_review' },
        { label: 'Active', value: 'active' },
        { label: 'Rejected', value: 'rejected' },
      ]
    },
    fields() {
      if (this.isCraDirectory) {
        return [
          { key: 'name', label: 'Name' },
          { key: 'businessNumber', label: 'Business number' },
          { key: 'city', label: 'City' },
          { key: 'province', label: 'Province' },
          { key: 'status', label: 'Status' },
          { key: 'countryId', label: 'Country' },
        ]
      }
      return [
        { key: 'actions', label: 'Actions' },
        { key: 'name', label: 'Name' },
        { key: 'id', label: 'ID' },
        { key: 'businessNumber', label: 'Business Number' },
        { key: 'registrationNumber', label: 'Registration / incorporation no.' },
        { key: 'requestedPlan', label: 'Requested plan' },
        { key: 'registrationKind', label: 'Registration' },
        { key: 'status', label: 'Status' },
        { key: 'progress', label: 'Progress' },
        { key: 'submittedBy', label: 'Submitted by' },
        { key: 'countryId', label: 'Country' },
        { key: 'createdAt', label: 'Created At' },
      ]
    },
    givelyIsAdministrator() {
      return this.advisory && this.advisory.accountAdministrator && this.advisory.accountAdministrator.kind === 'gively'
    },
    advisoryTeam() {
      return (this.advisory && this.advisory.teamMembers) || []
    },
    advisoryActivity() {
      return (this.advisory && this.advisory.activityLog) || []
    },
    teamFields() {
      return [
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'status', label: 'Status' },
        { key: 'addedAt', label: 'Added' },
        { key: 'userId', label: 'User id' },
      ]
    },
    activityFields() {
      return [
        { key: 'at', label: 'When' },
        { key: 'action', label: 'Action' },
        { key: 'userName', label: 'By' },
        { key: 'details', label: 'Details' },
      ]
    },
  },
  watch: {
    '$route.name'(name, previous) {
      if (name === previous) return
      if (name !== 'charities-manage' && name !== 'charities-cra') return
      this.resetListForRoute()
    },
    '$route.query.search'(value) {
      // Deep link from other pages (e.g. fundraiser → charity): re-filter in place.
      const next = typeof value === 'string' ? value : ''
      if (next !== this.search) {
        this.search = next
        this.filterCharities()
      }
    },
    range(newRange) {
      if (!newRange || typeof newRange !== 'string') {
        this.searchDate = ''
        return
      }
      const parts = newRange.split(' to ')
      if (parts.length === 2) {
        this.searchDate = { from: parts[0], to: parts[1] }
      } else if (parts[0] && parts[0].length === 10) {
        this.searchDate = { from: parts[0], to: parts[0] }
      } else {
        this.searchDate = ''
      }
    },
  },
  created() {
    this.items = Utils.completeTable()
    this.loadCountries()
    this.resetListForRoute()
  },
  beforeDestroy() {
    this.stopCraPoll()
  },
  methods: {
    resetListForRoute() {
      this.stopCraPoll()
      this.range = ''
      this.searchDate = ''
      this.search = typeof this.$route.query.search === 'string' ? this.$route.query.search : ''
      this.activatedBy = ''
      this.status = ''
      this.countryId = null
      this.craSync = null
      if (this.isCraDirectory) this.loadCraSyncStatus()
      this.filterCharities()
    },
    statusText(item) {
      if (!item) return ''
      if (!this.isCraDirectory) {
        return this.workflowLabels[item.workflowStatus] || this.statusLabels[item.status] || item.status || ''
      }
      return this.directoryStatusLabels[item.status] || this.statusLabels[item.status] || item.status || ''
    },
    statusVariant(item) {
      if (!item) return 'secondary'
      const key = !this.isCraDirectory && item.workflowStatus ? item.workflowStatus : item.status
      return this.statusVariants[key] || 'secondary'
    },
    progressText(item) {
      if (!item) return '—'
      if (item.workflowStatus === 'pending_creation') return 'Creation not finished'
      if (item.workflowStatus === 'pending_activation') {
        return this.stepLabels[item.activationStep] || 'Activation in progress'
      }
      if (item.status === 'under_review') return 'Waiting for Gively review'
      if (item.status === 'active') return 'Active'
      if (item.status === 'rejected') return 'Rejected'
      return '—'
    },
    loadCraSyncStatus() {
      API.getCraSyncStatus().then(res => {
        if (!res.success || !res.data) return
        const wasRunning = this.craUpdateRunning
        this.craSync = res.data
        if (res.data.running || res.data.status === 'running') {
          this.startCraPoll()
          return
        }
        this.stopCraPoll()
        if (wasRunning && res.data.status === 'succeeded') {
          const added = Number(res.data.added || 0)
          this.showToast(
            `CRA list updated. ${added.toLocaleString()} charities added.`,
            'CheckCircleIcon',
            'success',
          )
          this.getCharities(1)
        } else if (wasRunning && res.data.status === 'failed') {
          this.showToast(res.data.error || 'CRA update failed')
        }
      }).catch(() => {})
    },
    startCraPoll() {
      if (this.craPoll) return
      this.craPoll = setInterval(() => this.loadCraSyncStatus(), 5000)
    },
    stopCraPoll() {
      if (!this.craPoll) return
      clearInterval(this.craPoll)
      this.craPoll = null
    },
    confirmCraUpdate() {
      this.$bvModal.msgBoxConfirm(
        'Fetch the latest list published by the CRA? This can take several minutes. Charities the CRA has added since the last update will show here and in the website search used to activate a charity. Charities a user has already started are not overwritten.',
        {
          title: 'Update from CRA',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'primary',
          okTitle: 'Update',
          cancelTitle: 'Cancel',
          hideHeaderClose: false,
          centered: true,
        },
      ).then(confirmed => {
        if (confirmed) this.refreshCraRegistry()
      })
    },
    refreshCraRegistry() {
      this.craUpdateBusy = true
      API.refreshCraRegistry().then(res => {
        this.craUpdateBusy = false
        if (res.success && res.data) {
          this.craSync = {
            ...(this.craSync || {}),
            ...res.data,
            running: true,
            status: 'running',
          }
          this.startCraPoll()
          if (res.data.alreadyRunning) {
            this.showToast('A CRA update is already running', 'InfoIcon', 'info')
          } else {
            this.showToast('CRA update started', 'CheckCircleIcon', 'success')
          }
        } else {
          this.showToast(res.message || 'Failed to start the CRA update')
        }
      }).catch(() => {
        this.craUpdateBusy = false
        this.showToast()
      })
    },
    formatActivityDate(value) {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return '—'
      return d.toLocaleString()
    },
    activityLabel(action) {
      const labels = {
        'profile.updated': 'Profile updated',
        'profile.draft_submitted': 'Profile draft submitted',
        'profile.draft_approved': 'Profile draft approved',
        'profile.draft_rejected': 'Profile draft rejected',
        'campaign.created': 'Campaign created',
        'campaign.edited': 'Campaign edited',
        'campaign.approved': 'Campaign approved',
        'campaign.rejected': 'Campaign rejected',
        'team.member_added': 'Team member added',
        'team.member_removed': 'Team member removed',
        'team.invite_resent': 'Invitation re-sent',
        'team.invite_accepted': 'Invitation accepted',
        'team.gively_enabled': 'Gively advisory enabled',
        'team.gively_disabled': 'Gively advisory disabled',
        'plan.changed': 'Plan changed',
        'bank.updated': 'Bank details updated',
        'registration.completed': 'Registration completed',
        'review.resubmitted': 'Resubmitted for review',
      }
      return labels[action] || action
    },
    activityDetails(details) {
      if (!details || typeof details !== 'object') return ''
      return Object.entries(details)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
        .join(' · ')
    },
    planLabel(key) {
      const value = String(key || '').trim().toLowerCase()
      if (!value) return '—'
      return this.planLabels[value] || value
    },
    loadCountries() {
      API.getCountries({ page: 1, pageSize: 200 }).then(res => {
        if (res.success && res.data && res.data.list) {
          this.countryOptions = res.data.list.map(country => ({
            label: `${country.nameEn} / ${country.nameFr}`,
            value: country.id,
          }))
        }
      }).catch(() => {})
    },
    loadNewPage(e, page) {
      e.preventDefault()
      this.getCharities(page)
    },
    showCharityRows(list) {
      const visible = (list || []).filter(row => row && row.id && !this.removedCharityIds[row.id])
      this.items = Utils.completeTable(visible)
      this.tableKey += 1
    },
    getCharities(page = 1) {
      const requestId = this.charityListRequest + 1
      this.charityListRequest = requestId
      this.itemBusy = true
      API.getCharities({ ...this.searchParams, pageNum: page }).then(res => {
        if (requestId !== this.charityListRequest) return
        this.itemBusy = false
        if (res.success && res.data) {
          this.currentPage = page
          this.showCharityRows(res.data.list)
          this.totalRows = res.data.total || 0
        }
      }).catch(() => {
        if (requestId !== this.charityListRequest) return
        this.itemBusy = false
        this.showToast()
      })
    },
    filterCharities() {
      this.searchParams = {
        scope: this.isCraDirectory ? 'cra' : 'managed',
      }
      if (this.search) this.searchParams.search = this.search
      if (this.status) this.searchParams.status = this.status
      if (this.countryId) this.searchParams.countryId = this.countryId
      if (this.activatedBy) this.searchParams.activatedBy = this.activatedBy
      if (this.searchDate && this.searchDate.from) {
        this.searchParams.startDate = `${this.searchDate.from} 00:00:00`
        const to = this.searchDate.to || this.searchDate.from
        this.searchParams.endDate = `${to} 23:59:59`
      }
      this.getCharities(1)
    },
    openAdvisory(charity) {
      this.advisoryOpen = true
      this.advisoryBusy = true
      this.advisory = null
      this.advisoryCampaigns = []
      API.getCharity(charity.id).then(res => {
        this.advisoryBusy = false
        if (res.success && res.data) {
          this.advisory = {
            ...res.data,
            activation: res.data.activation || {},
            accountAdministrator: res.data.accountAdministrator || {},
            teamMembers: res.data.teamMembers || [],
            activityLog: res.data.activityLog || [],
          }
          const pending = res.data.pendingProfile || {}
          this.advisoryForm = {
            descriptionEn: pending.descriptionEn || res.data.descriptionEn || '',
            impactEn: pending.impactEn || res.data.impactEn || '',
            logoUrl: pending.logoUrl || res.data.logoUrl || '',
            generalDonationsEnabled: pending.generalDonationsEnabled === null || pending.generalDonationsEnabled === undefined
              ? Boolean(res.data.generalDonationsEnabled)
              : Boolean(pending.generalDonationsEnabled),
          }
          if (this.advisory.accountAdministrator.kind === 'gively') {
            this.loadAdvisoryCampaigns(charity.id)
          }
        } else {
          this.advisoryOpen = false
          this.showToast(res.message || 'Failed to load charity')
        }
      }).catch(() => {
        this.advisoryBusy = false
        this.advisoryOpen = false
        this.showToast()
      })
    },
    loadAdvisoryCampaigns(charityId) {
      API.listAdvisoryCampaigns({ charityId }).then(res => {
        if (res.success) this.advisoryCampaigns = res.data || []
      }).catch(() => {})
    },
    saveAdvisoryProfile() {
      if (!this.advisory) return
      this.advisoryBusy = true
      API.updateAdvisoryProfile(this.advisory.id, {
        locale: 'en',
        description: this.advisoryForm.descriptionEn,
        impact: this.advisoryForm.impactEn,
        logoUrl: this.advisoryForm.logoUrl,
        generalDonationsEnabled: this.advisoryForm.generalDonationsEnabled,
      }).then(res => {
        this.advisoryBusy = false
        if (res.success) {
          this.showToast('Profile submitted for the charity’s approval', 'CheckCircleIcon', 'success')
          this.openAdvisory(this.advisory)
        } else {
          this.showToast(res.message || 'Failed to submit profile')
        }
      }).catch(() => {
        this.advisoryBusy = false
        this.showToast()
      })
    },
    saveAdvisoryCampaign() {
      if (!this.advisory) return
      if (!this.campaignForm.title || !this.campaignForm.story || !this.campaignForm.targetAmount) {
        this.showToast('Title, story, and target amount are required')
        return
      }
      this.advisoryBusy = true
      API.createAdvisoryCampaign({
        charityId: this.advisory.id,
        title: this.campaignForm.title,
        story: this.campaignForm.story,
        targetAmount: this.campaignForm.targetAmount,
        categoryId: this.campaignForm.categoryId,
        coverPhoto: this.campaignForm.coverPhoto,
      }).then(res => {
        this.advisoryBusy = false
        if (res.success) {
          this.campaignForm = {
            title: '',
            story: '',
            targetAmount: '',
            categoryId: 'other',
            coverPhoto: '',
          }
          this.showToast('Campaign submitted for the charity’s approval', 'CheckCircleIcon', 'success')
          this.loadAdvisoryCampaigns(this.advisory.id)
        } else {
          this.showToast(res.message || 'Failed to submit campaign')
        }
      }).catch(() => {
        this.advisoryBusy = false
        this.showToast()
      })
    },
    canReviewCharity(charity) {
      return Boolean(charity) && (charity.status === 'pending' || charity.status === 'under_review')
    },
    canDeactivateCharity(charity) {
      return Boolean(charity) && ['pending', 'under_review', 'active', 'rejected'].includes(charity.status)
    },
    openReview(charity) {
      this.previewOpen = true
      this.previewBusy = true
      this.preview = null
      this.rejectionReason = ''
      API.getCharity(charity.id).then(res => {
        this.previewBusy = false
        if (res.success && res.data) {
          this.preview = {
            ...res.data,
            activation: res.data.activation || {},
          }
        } else {
          this.previewOpen = false
          this.showToast(res.message || 'Failed to load charity')
        }
      }).catch(() => {
        this.previewBusy = false
        this.previewOpen = false
        this.showToast()
      })
    },
    approveBankChange() {
      this.bankCode = ''
      this.bankPrompt = true
    },
    confirmBankChange(event) {
      const code = (this.bankCode || '').trim()
      if (!code || !this.preview) {
        if (event && event.preventDefault) event.preventDefault()
        return
      }
      this.previewBusy = true
      commonApi.staffStepUp({ code, recoveryCode: code }).then(step => {
        if (!step || !step.success || !step.data || !step.data.stepUpCode) {
          this.showToast('Authenticator confirmation failed')
          this.previewBusy = false
          return null
        }
        return API.approveBankChange(this.preview.id, { stepUpCode: step.data.stepUpCode })
      }).then(res => {
        if (!res) return
        this.previewBusy = false
        if (res.success && res.data && res.data.pendingApproval) {
          this.preview.activation.bankApprovalRequested = true
          this.showToast('Recorded. A different staff member must approve this bank change.', 'CheckCircleIcon', 'warning')
          return
        }
        if (res.success) {
          this.showToast('Bank change approved', 'CheckCircleIcon', 'success')
          this.openReview({ id: this.preview.id })
        } else {
          this.showToast(res.message || 'Could not approve the bank change')
        }
      }).catch(() => {
        this.previewBusy = false
        this.showToast()
      })
    },
    approvePreview() {
      if (!this.preview) return
      this.previewBusy = true
      API.approveCharity(this.preview.id).then(res => {
        this.previewBusy = false
        if (res.success && res.data) {
          this.replaceRow(res.data)
          this.previewOpen = false
          this.showToast('Organization approved. It can now fundraise once campaigns are reviewed.', 'CheckCircleIcon', 'success')
        } else {
          this.showToast(res.message || 'Failed to approve organization')
        }
      }).catch(() => {
        this.previewBusy = false
        this.showToast()
      })
    },
    rejectPreview() {
      if (!this.preview) return
      if (!this.rejectionReason || !this.rejectionReason.trim()) {
        this.showToast('Enter a rejection reason')
        return
      }
      this.previewBusy = true
      API.rejectCharity(this.preview.id, this.rejectionReason.trim()).then(res => {
        this.previewBusy = false
        if (res.success && res.data) {
          this.replaceRow(res.data)
          this.previewOpen = false
          this.showToast('Organization rejected', 'CheckCircleIcon', 'success')
        } else {
          this.showToast(res.message || 'Failed to reject organization')
        }
      }).catch(() => {
        this.previewBusy = false
        this.showToast()
      })
    },
    replaceRow(updated) {
      this.items = this.items.map(item => (item.id === updated.id ? { ...item, ...updated } : item))
    },
    confirmDelete(charity) {
      const name = charity.nameEn || 'this charity'
      this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete "${name}"?`,
        {
          title: `Delete ${name}`,
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          centered: true,
        },
      ).then(value => {
        if (value) this.deleteCharity(charity)
      })
    },
    deleteCharity(charity) {
      this.itemBusy = true
      API.deleteCharity(charity.id).then(res => {
        if (!res.success) {
          this.itemBusy = false
          this.showToast(res.message || 'Failed to delete charity')
          return
        }
        this.$set(this.removedCharityIds, charity.id, true)
        this.showToast(`"${charity.nameEn || 'Charity'}" was deleted`, 'CheckCircleIcon', 'success')
        const stillOnPage = this.items.some(item => item.id && item.id !== charity.id)
        const page = stillOnPage ? this.currentPage : Math.max(1, this.currentPage - 1)
        this.getCharities(page)
      }).catch(() => {
        this.itemBusy = false
        this.showToast()
      })
    },
    confirmDeactivate(charity) {
      this.$bvModal.msgBoxConfirm(
        `Deactivate "${charity.nameEn}"? This will remove the charity from public listings and release the account claim so it can be activated again.`,
        {
          title: 'Deactivate charity',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Deactivate',
          cancelTitle: 'Cancel',
          centered: true,
        },
      ).then(value => {
        if (value) {
          this.deactivateCharity(charity)
        }
      })
    },
    deactivateCharity(charity) {
      this.itemBusy = true
      API.deactivateCharity(charity.id).then(res => {
        this.itemBusy = false
        if (res.success) {
          this.items = this.items.map(item => {
            if (item.id === charity.id) {
              return res.data
            }
            return item
          })
          this.showToast('Charity deactivated successfully', 'CheckCircle', 'success')
        } else {
          this.showToast(res.message || 'Failed to deactivate charity')
        }
      }).catch(() => {
        this.itemBusy = false
        this.showToast()
      })
    },
    showToast(title = 'An error occurred', icon = 'AlertTriangleIcon', variant = 'danger') {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
